import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  AgentNodeMeta,
  AnalyticsNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from './VariablePicker';

interface VariableGroup {
  namespace: string;
  variables: { path: string; label: string }[];
}

const BASE_GROUPS: VariableGroup[] = [
  {
    namespace: 'call',
    variables: [
      { path: 'call.id', label: 'id' },
      { path: 'call.direction', label: 'direction' },
      { path: 'call.call_transport', label: 'call_transport' },
      { path: 'call.channel', label: 'channel' },
      { path: 'call.modality', label: 'modality' },
      { path: 'call.status', label: 'status' },
      { path: 'call.outcome', label: 'outcome' },
      { path: 'call.duration', label: 'duration' },
      { path: 'call.phone_number', label: 'phone_number' },
      { path: 'call.lead_data', label: 'lead_data' },
      { path: 'call.availability_data', label: 'availability_data' },
      { path: 'call.callback_data', label: 'callback_data' },
      { path: 'call.shift_data', label: 'shift_data' },
    ],
  },
  {
    namespace: 'contact',
    variables: [
      { path: 'contact.name', label: 'name' },
      { path: 'contact.phone', label: 'phone' },
    ],
  },
  {
    namespace: 'campaign',
    variables: [
      { path: 'campaign.name', label: 'name' },
      { path: 'campaign.id', label: 'id' },
    ],
  },
  {
    namespace: 'config',
    variables: [
      { path: 'config.notification_emails', label: 'notification_emails' },
      { path: 'config.high_intent_emails', label: 'high_intent_emails' },
    ],
  },
  {
    namespace: 'now (UTC, evaluated at run time)',
    variables: [
      { path: 'now', label: 'now (full ISO timestamp)' },
      { path: 'now_date', label: 'now_date (YYYY-MM-DD)' },
      { path: 'now_time', label: 'now_time (HH:MM)' },
      { path: 'now_hour', label: 'now_hour (0-23)' },
      { path: 'now_day_of_week', label: 'now_day_of_week (0=Sun … 6=Sat)' },
      { path: 'current_month', label: 'current_month (1st of month, YYYY-MM-DD)' },
    ],
  },
  {
    namespace: 'sms (global, last sms node wins)',
    variables: [
      { path: 'sms_sent', label: 'sms_sent (true if last SMS succeeded)' },
      { path: 'sms_not_sent', label: 'sms_not_sent (true if last SMS failed)' },
      { path: 'sms_sent_to', label: 'sms_sent_to (recipient list)' },
    ],
  },
  {
    namespace: 'per-node output (use node ID)',
    variables: [
      { path: 'nodes.<nodeId>.status', label: 'status (success/error/skipped)' },
      { path: 'nodes.<nodeId>.sms_sent', label: 'sms_sent (per SMS node)' },
      { path: 'nodes.<nodeId>.sms_not_sent', label: 'sms_not_sent (per SMS node)' },
      { path: 'nodes.<nodeId>.branch', label: 'branch (per condition node)' },
    ],
  },
  {
    namespace: 'enriched (scraped vehicle)',
    variables: [
      { path: 'review.enriched_vehicle.name', label: 'name' },
      { path: 'review.enriched_vehicle.trim', label: 'trim' },
      { path: 'review.enriched_vehicle.price', label: 'price' },
      { path: 'review.enriched_vehicle.registration', label: 'registration' },
      { path: 'review.enriched_vehicle.mileage', label: 'mileage' },
      { path: 'review.enriched_vehicle.fuel_type', label: 'fuel_type' },
      { path: 'review.enriched_vehicle.body_type', label: 'body_type' },
      { path: 'review.enriched_vehicle.engine', label: 'engine' },
      { path: 'review.enriched_vehicle.transmission', label: 'transmission' },
      { path: 'review.enriched_vehicle.doors', label: 'doors' },
      { path: 'review.enriched_vehicle.exterior_color', label: 'exterior_color' },
      { path: 'review.enriched_vehicle.num_owners', label: 'num_owners' },
      { path: 'review.enriched_vehicle.co2_emissions', label: 'co2_emissions' },
      { path: 'review.enriched_vehicle.fuel_efficiency', label: 'fuel_efficiency' },
      { path: 'review.enriched_vehicle.power', label: 'power' },
      { path: 'review.enriched_vehicle.top_speed', label: 'top_speed' },
      { path: 'review.enriched_vehicle.location', label: 'location' },
      { path: 'review.enriched_vehicle.checkout_url', label: 'checkout_url (reservation link)' },
    ],
  },
];

/**
 * Recursively extract all variable paths from a JSON schema's properties.
 * Produces both parent paths (e.g. review.lead_data) and nested leaf paths
 * (e.g. review.lead_data.customer_name, review.lead_data.selected_vehicle.vdp_url).
 */
function extractSchemaVariables(
  properties: Record<string, unknown>,
  prefix: string,
): { path: string; label: string }[] {
  const result: { path: string; label: string }[] = [];

  for (const [key, propDef] of Object.entries(properties)) {
    const fullPath = `${prefix}.${key}`;
    result.push({ path: fullPath, label: key });

    const prop = propDef as Record<string, unknown> | undefined;
    if (!prop) continue;

    // Recurse into object properties
    if (prop.type === 'object' && prop.properties) {
      result.push(...extractSchemaVariables(prop.properties as Record<string, unknown>, fullPath));
    }

    // Recurse into array item properties (e.g. array of objects)
    if (prop.type === 'array' && prop.items) {
      const items = prop.items as Record<string, unknown>;
      if (items.properties) {
        result.push(
          ...extractSchemaVariables(items.properties as Record<string, unknown>, fullPath),
        );
      }
    }
  }

  return result;
}

const STANDARD_REVIEW_FIELDS = new Set([
  'reviewed_outcome',
  'outcome_matches',
  'reviewed_lead_data',
  'lead_data',
  'lead_data_matches',
  'reviewed_availability',
  'availability_data',
  'availability_matches',
  'discrepancies',
  'analysis',
  'confidence_score',
  'generated_transcript',
]);

function buildReviewGroupFromMeta(meta: ReviewNodeMeta): VariableGroup {
  const { outputKey: key, schema, isAudioMode } = meta;
  const base: { path: string; label: string }[] = [
    { path: `${key}.reviewed_outcome`, label: 'reviewed_outcome' },
    { path: `${key}.ai_analysis`, label: 'ai_analysis' },
    { path: `${key}.confidence_score`, label: 'confidence_score' },
    { path: `${key}.additional_review_data`, label: 'additional_review_data' },
  ];

  if (isAudioMode) {
    base.push(
      { path: `${key}.generated_transcript`, label: 'generated_transcript' },
      {
        path: `${key}.generated_transcript.fullTranscript`,
        label: 'generated_transcript.fullTranscript',
      },
    );
  }

  if (schema && typeof schema === 'object') {
    const properties = (schema as { properties?: Record<string, unknown> }).properties ?? {};
    for (const [field, propDef] of Object.entries(properties)) {
      const vars = extractSchemaVariables({ [field]: propDef }, key);
      base.push(...vars);
      if (!STANDARD_REVIEW_FIELDS.has(field)) {
        base.push(...extractSchemaVariables({ [field]: propDef }, `${key}.additional_review_data`));
      }
    }
  }

  return { namespace: key, variables: base };
}

function buildReviewGroups(
  metas?: ReviewNodeMeta[],
  fallbackSchema?: Record<string, unknown>,
): VariableGroup[] {
  if (metas && metas.length > 0) {
    return metas.map((m) => buildReviewGroupFromMeta(m));
  }
  return [
    buildReviewGroupFromMeta({ outputKey: 'review', schema: fallbackSchema, isAudioMode: false }),
  ];
}

/**
 * Walk a flat JSON schema and split properties by type for classifier/quality
 * autocomplete. Booleans → `<prefix>.<bucket>.<key>` (labels|flags),
 * non-booleans → `<prefix>.data.<key>`. Matches BaseAgent.splitByType.
 */
function partitionByType(
  properties: Record<string, unknown>,
  prefix: string,
  boolBucket: 'labels' | 'flags',
): { path: string; label: string }[] {
  const out: { path: string; label: string }[] = [];
  for (const [key, propDef] of Object.entries(properties)) {
    const prop = propDef as Record<string, unknown> | undefined;
    const isBool = prop?.type === 'boolean';
    const bucket = isBool ? boolBucket : 'data';
    const fullPath = `${prefix}.${bucket}.${key}`;
    out.push({ path: fullPath, label: `${bucket}.${key}` });
    if (!isBool && prop?.type === 'object' && prop.properties) {
      out.push(...extractSchemaVariables(prop.properties as Record<string, unknown>, fullPath));
    }
    if (!isBool && prop?.type === 'array' && prop.items) {
      const items = prop.items as Record<string, unknown>;
      if (items.properties) {
        out.push(...extractSchemaVariables(items.properties as Record<string, unknown>, fullPath));
      }
    }
  }
  return out;
}

function buildClassifierGroupFromMeta(meta: ClassifierNodeMeta): VariableGroup {
  const { outputKey: key, taxonomy } = meta;
  const base: { path: string; label: string }[] = [
    { path: `${key}.confidenceScore`, label: 'confidenceScore' },
    { path: `${key}.taxonomyVersion`, label: 'taxonomyVersion' },
    { path: `${key}.classifierConfigId`, label: 'classifierConfigId' },
  ];
  if (taxonomy && typeof taxonomy === 'object') {
    const properties = (taxonomy as { properties?: Record<string, unknown> }).properties ?? {};
    base.push(...partitionByType(properties, key, 'labels'));
  }
  return { namespace: `${key} (classifier)`, variables: base };
}

// Mirrors buildAgentGroupFromMeta in VariablePicker: an agent node's fields sit
// at the top level of its context entry, not under a labels/data wrapper.
function buildAgentGroupFromMeta(meta: AgentNodeMeta): VariableGroup {
  const { outputKey: key, outputSchema } = meta;
  const properties =
    outputSchema && typeof outputSchema === 'object'
      ? ((outputSchema as { properties?: Record<string, unknown> }).properties ?? {})
      : {};
  const variables = Object.keys(properties).map((name) => ({
    path: `${key}.${name}`,
    label: name,
  }));
  variables.push(
    { path: `${key}._meta.confidenceScore`, label: '_meta.confidenceScore' },
    { path: `${key}._meta.reasoning`, label: '_meta.reasoning' },
  );
  return { namespace: `${key} (agent)`, variables };
}

function buildQualityGroupFromMeta(meta: QualityNodeMeta): VariableGroup {
  const { outputKey: key, rubric } = meta;
  const base: { path: string; label: string }[] = [
    { path: `${key}.reasoning`, label: 'reasoning' },
    { path: `${key}.confidenceScore`, label: 'confidenceScore' },
    { path: `${key}.rubricVersion`, label: 'rubricVersion' },
    { path: `${key}.qualityConfigId`, label: 'qualityConfigId' },
  ];
  if (rubric && typeof rubric === 'object') {
    const properties = (rubric as { properties?: Record<string, unknown> }).properties ?? {};
    base.push(...partitionByType(properties, key, 'flags'));
  }
  return { namespace: `${key} (quality)`, variables: base };
}

/**
 * Extracts the partial search term for autocomplete.
 * Handles both initial `{{` trigger and sub-expressions after `||`, `?`, `:` operators.
 * Returns insertStart (where to begin replacement) and query (search filter text).
 */
function getAutocompleteState(
  text: string,
  cursorPos: number,
): { triggerStart: number; insertStart: number; query: string } | null {
  const before = text.slice(0, cursorPos);
  const triggerIdx = before.lastIndexOf('{{');
  if (triggerIdx === -1) return null;

  // Make sure there's no closing `}}` between the `{{` and cursor
  const between = before.slice(triggerIdx + 2);
  if (between.includes('}}')) return null;

  // Check if we're after an operator (||, ?, :) — autocomplete the sub-expression
  const lastOperatorMatch = between.match(/.*(\|\||[?:])\s*/);
  if (lastOperatorMatch) {
    const operatorEnd = between.lastIndexOf(lastOperatorMatch[1]) + lastOperatorMatch[1].length;
    const afterOperator = between.slice(operatorEnd).trimStart();
    const insertPos =
      triggerIdx +
      2 +
      operatorEnd +
      (between.slice(operatorEnd).length - between.slice(operatorEnd).trimStart().length);
    return { triggerStart: triggerIdx, insertStart: insertPos, query: afterOperator };
  }

  return { triggerStart: triggerIdx, insertStart: triggerIdx, query: between };
}

function buildAnalyticsGroupFromMeta(meta: AnalyticsNodeMeta): VariableGroup {
  const { outputKey: key, metricKeys } = meta;
  const base: { path: string; label: string }[] = metricKeys.flatMap((mk) => [
    { path: `${key}.${mk}.count`, label: `${mk}.count` },
    { path: `${key}.${mk}.inbound`, label: `${mk}.inbound` },
    { path: `${key}.${mk}.outbound`, label: `${mk}.outbound` },
    { path: `${key}.${mk}.change`, label: `${mk}.change` },
  ]);
  return { namespace: `${key} (analytics)`, variables: base };
}

export interface VariableAutocompleteProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewOutputKeys?: string[];
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  className?: string;
  inputClassName?: string;
}

export const VariableAutocomplete = ({
  id,
  value,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  placeholder,
  multiline = false,
  rows = 3,
  className,
  inputClassName,
}: VariableAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [triggerStart, setTriggerStart] = useState(0);
  const [insertStart, setInsertStart] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reviewGroups = buildReviewGroups(reviewNodeMetas, responseFieldsSchema);
  const classifierGroups = (classifierNodeMetas ?? []).map(buildClassifierGroupFromMeta);
  const qualityGroups = (qualityNodeMetas ?? []).map(buildQualityGroupFromMeta);
  const agentGroups = (agentNodeMetas ?? []).map(buildAgentGroupFromMeta);
  const analyticsGroups = (analyticsNodeMetas ?? []).map(buildAnalyticsGroupFromMeta);
  const allGroups: VariableGroup[] = [
    BASE_GROUPS[0],
    ...reviewGroups,
    ...classifierGroups,
    ...qualityGroups,
    ...agentGroups,
    ...analyticsGroups,
    ...BASE_GROUPS.slice(1),
  ];
  const allVariables = allGroups.flatMap((g) => g.variables);

  const filteredGroups: VariableGroup[] = allGroups
    .map((g) => ({
      ...g,
      variables: g.variables.filter(
        (v) => query === '' || v.path.toLowerCase().includes(query.toLowerCase()),
      ),
    }))
    .filter((g) => g.variables.length > 0);

  const flatFiltered = filteredGroups.flatMap((g) => g.variables);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const selectVariable = useCallback(
    (path: string) => {
      const el = inputRef.current ?? textareaRef.current;
      const cursorPos = el?.selectionStart ?? value.length;
      // If inserting after an operator (||, ?, :), insert just the path without {{ }}
      const isSubExpression = insertStart !== triggerStart;
      const insertion = isSubExpression ? path : `{{${path}}}`;
      const replaceFrom = isSubExpression ? insertStart : triggerStart;
      const next = value.slice(0, replaceFrom) + insertion + value.slice(cursorPos);
      onChange(next);
      closeDropdown();

      // Restore focus and set cursor after insertion
      setTimeout(() => {
        if (el) {
          const newPos = triggerStart + insertion.length;
          el.selectionStart = el.selectionEnd = newPos;
          el.focus();
        }
      }, 0);
    },
    [value, triggerStart, insertStart, onChange, closeDropdown],
  );

  const handleChange = (newValue: string, cursorPos: number) => {
    onChange(newValue);
    const state = getAutocompleteState(newValue, cursorPos);
    if (state) {
      setTriggerStart(state.triggerStart);
      setInsertStart(state.insertStart);
      setQuery(state.query);
      setOpen(true);
      setActiveIndex(0);
    } else {
      closeDropdown();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatFiltered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const target = flatFiltered[activeIndex];
      if (target) selectVariable(target.path);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  };

  const handleBlur = () => {
    // Delay so that a click on a dropdown item registers before we close
    closeTimerRef.current = setTimeout(() => {
      closeDropdown();
    }, 150);
  };

  const handleDropdownMouseDown = () => {
    // Cancel the blur-triggered close when user clicks inside dropdown
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  };

  // Keep active index in bounds when filtered list changes
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(flatFiltered.length - 1, 0)));
  }, [flatFiltered.length]);

  // Scroll active item into view
  useEffect(() => {
    if (!open) return;
    const el = dropdownRef.current?.querySelector(`[data-active="true"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const baseInputClass =
    'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none';

  const sharedProps = {
    id,
    placeholder,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
  };

  // Track flat index across groups for aria-active
  let flatIndex = 0;

  return (
    <div className={`relative ${className ?? ''}`}>
      {multiline ? (
        <textarea
          ref={textareaRef}
          value={value}
          rows={rows}
          className={`${baseInputClass} ${inputClassName ?? ''}`}
          onChange={(e) => handleChange(e.target.value, e.target.selectionStart ?? value.length)}
          {...sharedProps}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={value}
          className={`${baseInputClass} ${inputClassName ?? ''}`}
          onChange={(e) => handleChange(e.target.value, e.target.selectionStart ?? value.length)}
          {...sharedProps}
        />
      )}

      {open && flatFiltered.length > 0 && (
        <div
          ref={dropdownRef}
          onMouseDown={handleDropdownMouseDown}
          className="border-border bg-background absolute top-full left-0 z-50 mt-1 max-h-72 w-56 overflow-y-auto rounded-md border shadow-lg"
        >
          {filteredGroups.map((group) => (
            <div key={group.namespace}>
              <div className="bg-muted/50 text-muted-foreground px-3 py-1.5 text-[10px] font-semibold tracking-wider uppercase">
                {group.namespace}
              </div>
              {group.variables.map((v) => {
                const isActive = flatIndex === activeIndex;
                const currentIndex = flatIndex;
                flatIndex++;
                return (
                  <button
                    key={v.path}
                    type="button"
                    data-active={isActive ? 'true' : undefined}
                    className={`w-full px-3 py-1.5 text-left font-mono text-xs ${
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }`}
                    onMouseEnter={() => setActiveIndex(currentIndex)}
                    onClick={() => selectVariable(v.path)}
                  >
                    {`{{${v.path}}}`}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {open && allVariables.length > 0 && filteredGroups.length === 0 && (
        <div className="border-border bg-background absolute top-full left-0 z-50 mt-1 w-56 rounded-md border shadow-lg">
          <p className="text-muted-foreground px-3 py-2 text-xs italic">No variables match</p>
        </div>
      )}
    </div>
  );
};
